import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowLeft, User, Mail, Phone, Key, Save, CheckCircle2 } from 'lucide-react';
import HomeButton from '../components/HomeButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MiPerfil = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    nombre_completo: '',
    email: '',
    telefono: '',
    password: '',
    confirm_password: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        nombre_completo: user.nombre_completo || '',
        email: user.email || '',
        telefono: user.telefono || '',
        password: '',
        confirm_password: ''
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate passwords if provided
    if (formData.password || formData.confirm_password) {
      if (formData.password !== formData.confirm_password) {
        setError('Las contraseñas no coinciden');
        setLoading(false);
        return;
      }
      if (formData.password.length < 6) {
        setError('La contraseña debe tener al menos 6 caracteres');
        setLoading(false);
        return;
      }
    }

    try {
      const token = localStorage.getItem('token');
      const updateData = {
        nombre_completo: formData.nombre_completo,
        email: formData.email,
        telefono: formData.telefono
      };

      // Only include password if provided
      if (formData.password) {
        updateData.password = formData.password;
      }

      await axios.put(`${API}/usuarios/me`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Perfil actualizado exitosamente');
      
      // Clear password fields
      setFormData(prev => ({
        ...prev,
        password: '',
        confirm_password: ''
      }));

      // If email changed, suggest logout and re-login
      if (formData.email !== user.email) {
        setTimeout(() => {
          alert('Tu email ha sido actualizado. Por favor, cierra sesión e inicia sesión nuevamente con tu nuevo email.');
        }, 1000);
      }

    } catch (err) {
      setError(err.response?.data?.detail || 'Error al actualizar el perfil');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
          <HomeButton />
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <User className="h-6 w-6" />
              Mi Perfil
            </CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              Actualiza tu información personal y credenciales
            </p>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert className="mb-4 bg-green-50 text-green-900 border-green-200">
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>{success}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Información Personal */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2">Información Personal</h3>
                
                <div>
                  <Label htmlFor="nombre_completo">
                    <User className="inline h-4 w-4 mr-2" />
                    Nombre Completo *
                  </Label>
                  <Input
                    id="nombre_completo"
                    value={formData.nombre_completo}
                    onChange={(e) => setFormData({ ...formData, nombre_completo: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="email">
                    <Mail className="inline h-4 w-4 mr-2" />
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    ⚠️ Si cambias el email, deberás iniciar sesión nuevamente
                  </p>
                </div>

                <div>
                  <Label htmlFor="telefono">
                    <Phone className="inline h-4 w-4 mr-2" />
                    Teléfono
                  </Label>
                  <Input
                    id="telefono"
                    type="tel"
                    value={formData.telefono}
                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                    placeholder="+56912345678"
                    className="mt-1"
                  />
                </div>

                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Rol:</strong> {user?.rol === 'administrador' ? 'Administrador' : user?.rol}
                  </p>
                </div>
              </div>

              {/* Cambiar Contraseña */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2 flex items-center gap-2">
                  <Key className="h-5 w-5" />
                  Cambiar Contraseña (Opcional)
                </h3>
                
                <div>
                  <Label htmlFor="password">Nueva Contraseña</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Dejar en blanco para mantener actual"
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Mínimo 6 caracteres. Dejar vacío si no deseas cambiarla.
                  </p>
                </div>

                <div>
                  <Label htmlFor="confirm_password">Confirmar Nueva Contraseña</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={formData.confirm_password}
                    onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                    placeholder="Repetir nueva contraseña"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" disabled={loading} className="flex-1">
                  <Save className="mr-2 h-4 w-4" />
                  {loading ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MiPerfil;
